/**
 * Services Management Page
 * Full CRUD implementation for IT services
 */

import { isBlankHtml } from "utils/htmlContent";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import ImageUpload from "components/ImageUpload";
import RichTextEditor from "components/RichTextEditor";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { adminApi } from "services/api";
import { emptyServiceForm, descriptionPreview } from "./serviceForm";

// Below md the DataTable shows each row as a card, which would print long text in full; clamp it
// there (column `clampLines`, default 3) so cards stay scannable. Desktop cells are unchanged.
function ClampedCell({ value, column }) {
  return (
    <MDBox
      component="span"
      color="inherit"
      sx={({ breakpoints }) => ({
        [breakpoints.down("md")]: {
          display: "-webkit-box",
          WebkitLineClamp: column.clampLines || 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        },
      })}
    >
      {value}
    </MDBox>
  );
}

ClampedCell.propTypes = {
  value: PropTypes.node,
  column: PropTypes.shape({ clampLines: PropTypes.number }).isRequired,
};

// Bigger hit area for the row's "more" icon on touch screens (below md only).
const phoneTapTarget = ({ breakpoints }) => ({
  [breakpoints.down("md")]: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 40,
    minHeight: 40,
  },
});

// Full-screen dialog (below sm): let the form content scroll between a pinned title and action bar.
const fullScreenForm = { display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 };

function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(emptyServiceForm());
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [menu, setMenu] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await adminApi.getServices();
      setServices(response);
      setLoading(false);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to fetch services");
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditMode(false);
    setFormData(emptyServiceForm());
    setError("");
  };

  const handleEdit = (service) => {
    setOpen(true);
    setEditMode(true);
    setCurrentId(service._id);
    setFormData({
      title: service.title,
      slug: service.slug,
      shortDescription: service.shortDescription || "",
      fullDescription: service.fullDescription || "",
      features: service.features?.join("\n") || "",
      technologies: service.technologies?.join("\n") || "",
      benefits: service.benefits?.join("\n") || "",
      image: service.image || "",
      icon: service.icon || "",
      order: service.order || 0,
      featured: !!service.featured,
      showOnMenu: service.showOnMenu !== undefined ? !!service.showOnMenu : true,
    });
    setError("");
    handleCloseMenu();
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(emptyServiceForm());
    setEditMode(false);
    setCurrentId(null);
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "title" && !editMode) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setFormData({ ...formData, title: value, slug });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.slug) {
      setError("Title and slug are required");
      return;
    }
    if (isBlankHtml(formData.shortDescription) || isBlankHtml(formData.fullDescription)) {
      setError("Short and full descriptions are required");
      return;
    }

    const submitData = {
      title: formData.title,
      slug: formData.slug,
      shortDescription: formData.shortDescription,
      fullDescription: formData.fullDescription,
      features: formData.features.split("\n").filter((f) => f.trim()),
      technologies: formData.technologies.split("\n").filter((t) => t.trim()),
      benefits: formData.benefits.split("\n").filter((b) => b.trim()),
      icon: formData.icon,
      image: formData.image,
      order: parseInt(formData.order) || 0,
      featured: !!formData.featured,
      showOnMenu: !!formData.showOnMenu,
    };

    try {
      if (editMode) {
        await adminApi.updateService(currentId, submitData);
        setSuccess("Service updated successfully");
      } else {
        await adminApi.createService(submitData);
        setSuccess("Service created successfully");
      }
      handleClose();
      fetchServices();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this service?")) {
      try {
        await adminApi.deleteService(id);
        setSuccess("Service deleted successfully");
        fetchServices();
        setTimeout(() => setSuccess(""), 3000);
      } catch (err) {
        setError(err.response?.data?.error || "Delete failed");
      }
    }
    handleCloseMenu();
  };

  const openMenu = (event, service) => {
    setMenu(event.currentTarget);
    setSelectedService(service);
  };

  const handleCloseMenu = () => {
    setMenu(null);
    setSelectedService(null);
  };

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={Boolean(menu)}
      onClose={handleCloseMenu}
    >
      <MenuItem onClick={() => handleEdit(selectedService)}>Edit</MenuItem>
      <MenuItem onClick={() => handleDelete(selectedService._id)}>Delete</MenuItem>
    </Menu>
  );

  const columns = [
    { Header: "title", accessor: "title", width: "30%", Cell: ClampedCell },
    { Header: "slug", accessor: "slug", width: "25%", Cell: ClampedCell, clampLines: 2 },
    { Header: "description", accessor: "description", width: "35%" },
    { Header: "actions", accessor: "actions", width: "10%", align: "center" },
  ];

  const rows = services.map((service) => ({
    title: service.title,
    slug: service.slug,
    description: descriptionPreview(service),
    actions: (
      <MDTypography
        component="a"
        href="#"
        variant="caption"
        color="secondary"
        fontWeight="medium"
        aria-label="Service actions"
        sx={phoneTapTarget}
        onClick={(e) => {
          e.preventDefault();
          openMenu(e, service);
        }}
      >
        <Icon>more_vert</Icon>
      </MDTypography>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox mb={3}>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Card>
            <MDBox
              p={{ xs: 2, md: 3 }}
              display="flex"
              flexWrap="wrap"
              gap={1.5}
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">IT Services</MDTypography>
              <MDButton variant="gradient" color="info" onClick={handleOpen}>
                <Icon>add</Icon>&nbsp; Add Service
              </MDButton>
            </MDBox>
            <DataTable table={{ columns, rows }} canSearch />
          </Card>
        </MDBox>
      </MDBox>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>{editMode ? "Edit Service" : "Add New Service"}</DialogTitle>
        <form onSubmit={handleSubmit} style={fullScreenDialog ? fullScreenForm : undefined}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <MDBox mb={2}>
              <MDInput
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <RichTextEditor
                label="Short Description"
                value={formData.shortDescription}
                onChange={(value) => setFormData({ ...formData, shortDescription: value })}
                helperText="A brief summary of the service (1-2 sentences)"
                height={120}
              />
            </MDBox>
            <MDBox mb={2}>
              <RichTextEditor
                label="Full Description"
                value={formData.fullDescription}
                onChange={(value) => setFormData({ ...formData, fullDescription: value })}
                helperText="Detailed description with rich formatting, images, and media"
                height={300}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Features (one per line)"
                name="features"
                value={formData.features}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Technologies (one per line)"
                name="technologies"
                value={formData.technologies}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Benefits (one per line)"
                name="benefits"
                value={formData.benefits}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
              />
            </MDBox>
            <MDBox mb={2}>
              <ImageUpload
                currentImage={formData.image}
                onImageChange={(url) => setFormData({ ...formData, image: url })}
                label="Service Image"
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Icon (FontAwesome class)"
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                fullWidth
                placeholder="fas fa-laptop-code"
              />
            </MDBox>
            <MDBox mb={2} display="flex" flexWrap="wrap" rowGap={1} alignItems="center">
              <input
                type="checkbox"
                id="featured"
                name="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                style={{ marginRight: 8 }}
              />
              <label htmlFor="featured" style={{ marginRight: 24 }}>
                Featured Service
              </label>
              <input
                type="checkbox"
                id="showOnMenu"
                name="showOnMenu"
                checked={formData.showOnMenu}
                onChange={(e) => setFormData({ ...formData, showOnMenu: e.target.checked })}
                style={{ marginRight: 8 }}
              />
              <label htmlFor="showOnMenu">Show on menu</label>
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Display Order"
                name="order"
                type="number"
                value={formData.order}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleClose} color="secondary">
              Cancel
            </MDButton>
            <MDButton type="submit" variant="gradient" color="info">
              {editMode ? "Update" : "Create"}
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>

      {renderMenu}
    </DashboardLayout>
  );
}

export default Services;
